import React, { useCallback, useState } from 'react'
import Toggle from 'react-toggle'
import classnames from 'classnames'

import useAuthState from 'hooks/useAuthState'
import useUpdateUser from 'hooks/useUpdateUser'
import { MEMBERSHIP_STATUS, WAITLIST_STATUS } from 'helpers/constants'

import { ProfileImage } from 'components'

import 'react-toggle/style.css'
import { apiManager } from 'helpers'
import message from 'antd/lib/message'

const WAITLIST_STATUS_LABELS = {
  [WAITLIST_STATUS.DATABASE_APPROVED]: 'Free Database',
  [WAITLIST_STATUS.YES]: 'Active',
  DEFAULT: 'On Waitlist',
}

const Divider = () => <div className="h-0.5 my-3 w-full bg-white opacity-10" />

const Title = ({ children }) => <div className="text-sm opacity-60 mb-0.5">{children}</div>
const Subtitle = ({ children }) => <div className="font-semibold text-base">{children}</div>

const Section = ({ children }) => (
  <div className="bg-woodsmokeBlack rounded-lg p-4 w-full mb-5">{children}</div>
)

const Membership = () => {
  const { user } = useAuthState()
  const { updateUserData } = useUpdateUser()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [membershipStatus, setMembershipStatus] = useState(user.membershipStatus)

  const isMembershipActive = membershipStatus === MEMBERSHIP_STATUS.ACTIVE

  const handleToggle = useCallback(
    async e => {
      e.preventDefault()
      const membershipStatus = e.target.checked
        ? MEMBERSHIP_STATUS.ACTIVE
        : MEMBERSHIP_STATUS.PAUSED
      setMembershipStatus(membershipStatus)

      try {
        setIsSubmitting(true)
        const response = await apiManager.updateMembershipStatus({
          membershipStatus,
        })
        updateUserData(response)
        console.log(response)

        message.success('Status updated successfully', 2.5)
      } catch (error) {
        message.destroy()
        message.error(error.message || 'Please try again')
      } finally {
        setIsSubmitting(false)
      }
    },
    [updateUserData]
  )

  return (
    <div className="flex justify-center font-ownersWide px-4 text-white">
      <div className="w-full" style={{ maxWidth: '500px' }}>
        <Section>
          <div className="flex items-center">
            <ProfileImage
              className="w-16 h-16 m-0 rounded-full object-cover mr-4"
              src={user.profileImage}
            />
            <div className="text-xl font-semibold">{user.name}</div>
          </div>
          <Divider />
          <div>
            <Title>Membership</Title>
            <Subtitle>
              {WAITLIST_STATUS_LABELS[user.waitlistStatus] || WAITLIST_STATUS_LABELS.DEFAULT}
            </Subtitle>
          </div>
          <Divider />
          <div>
            <Title>Joined</Title>
            <Subtitle>
              {new Date(user.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Subtitle>
          </div>
          {user.inviteReferralCode && (
            <>
              <Divider />
              <div>
                <Title>Referral Code</Title>
                <Subtitle>{user.inviteReferralCode}</Subtitle>
              </div>
            </>
          )}
        </Section>
        {user.waitlistStatus === WAITLIST_STATUS.DATABASE_APPROVED && (
          <div>
            <div className="mb-1 font-lg font-semibold">Manage Account</div>
            <Section>
              <div className="font-semibold mb-1">Free Database</div>
              <div className="flex items-end mb-3">
                <div className="opacity-60 pr-5 text-sm">
                  Visible in Discover to other members of TELEPORT, and remain eligible for upcoming
                  matchmaking in-app events.
                </div>
                <div>
                  <Toggle
                    checked={isMembershipActive}
                    icons={false}
                    disabled={isSubmitting}
                    onChange={handleToggle}
                  />
                </div>
              </div>
              <div
                className={classnames(
                  'px-4 py-2 rounded-full border border-solid border-regentGray font-semibold inline-block',
                  {
                    'text-laPalmaGreen': isMembershipActive,
                    'opacity-50': !isMembershipActive,
                  }
                )}
              >
                {isMembershipActive ? 'Enabled' : 'Disabled'}
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  )
}

export default Membership
